import { Navigate, Outlet, Route, useParams } from 'react-router-dom';

import ChartCreatePage from '@/features/(charts)/chart-detail/pages/chart-create-page';
import ChartDetailPage from '@/features/(charts)/chart-detail/pages/chart-detail-page';
import {
  IssueDetailPage,
  StaticIssueDetailPage,
} from '@/features/(signal)/signal-issue-detail';
import { SignalsLayout } from '@/features/(signal)/signals';
import CompanyDetailRedirection from '@/features/Account/components/CompanyDetailRedirection';
import CompanyPage from '@/features/Account/components/CompanyListPage';
import CompanyUploadPage from '@/features/Account/components/CompanyUpload';
import AgentDetailRedirection from '@/features/Agent/components/AgentDetail/AgentDetailRedirection';
import AgentPage from '@/features/Agent/pages/AgentListPage';
import CustomerDetailRedirection from '@/features/Contact/CustomerDetailRedirection';
import CustomerPage from '@/features/Contact/CustomerListPage';
import CustomerUploadPage from '@/features/Contact/CustomerUpload';
import CustomerSentimentPage from '@/features/CustomerSentiment/CustomerSentimentPage';
import Dashboard2DetailPage from '@/features/Dashboard/Dashboard2DetailPage';
import Dashboard2IndexPage from '@/features/Dashboard/Dashboard2IndexPage';
import Dashboard2ListPage from '@/features/Dashboard/Dashboard2ListPage';
import ExpPage from '@/features/Exp/ExpPage';
import FeedbackCsvUpload from '@/features/Feedback/components/FeedbackCsvUpload';
import FeedbackJsonUpload from '@/features/Feedback/components/FeedbackJsonUpload';
import FeedbackDetailRedirection from '@/features/Feedback/FeedbackDetailRedirection';
import AllFeedbackPage from '@/features/Feedback/FeedbackListPage';
import { ChatPage } from '@/features/hey-syncly/pages/chat-page';
import { InsightExplorerContent } from '@/features/InsightExplorer/components/Content';
import { InsightExplorerLayout } from '@/features/InsightExplorer/components/InsightExplorerLayout';
import { ProductRankingPage } from '@/features/ProductRanking/pages';
import ReportDetailPage from '@/features/Report/pages/ReportDetailPage';
import ReportListPage from '@/features/report-list';
import { StudioItemDetailPage } from '@/features/Studio/pages/StudioItemDetailPage';
import {
  TaxonomyDetailWorkspaceLayoutRoutePage,
  TaxonomyDetailWorkspaceRoutePage,
  TaxonomyWorkspaceRoutePage,
} from '@/features/Taxonomy/taxonomy-route-pages';
import TextSearchSandboxContainer from '@/features/TextSearchSandbox/TextSearchSandboxContainer';
import WorkflowPage from '@/features/Workflow';
import CreatePeriodicWorkflowPage from '@/features/Workflow/create-periodic';
import CreateRealtimeWorkflowPage from '@/features/Workflow/create-realtime';
import CreateSignalAlertPage from '@/features/Workflow/create-signal-alert';
import CreateSubscription2WorkflowPage from '@/features/Workflow/create-subscription2';
import WorkflowDetailPage from '@/features/Workflow/detail';
import DetailRedirection from '@/features/Workspace/DetailRedirection';
import WorkspaceHome from '@/features/Workspace/WorkspaceHome';
import WorkspaceLayout from '@/features/Workspace/WorkspaceLayout';
import WorkspaceRedirection from '@/features/Workspace/WorkspaceRedirection';
import SuspenseLayout from '@/layouts/SuspenseLayout';
import { SignalsPage } from '@/pages/workspace/signals';

import settingRoutes from './settingRoutes';

const StudioReportsRedirect = () => {
  const { studio_item_alias, workspace_id } = useParams();

  if (!workspace_id) {
    throw new Error('workspace_id is required');
  }

  if (!studio_item_alias) {
    return <Navigate to={`/workspace/${workspace_id}/reports`} replace />;
  }

  return (
    <Navigate
      to={`/workspace/${workspace_id}/reports/${studio_item_alias}`}
      replace
    />
  );
};

const workspaceRoutes = (
  <Route path="workspace" element={<WorkspaceLayout />}>
      <Route element={<SuspenseLayout />}>
        <Route index element={<WorkspaceRedirection />} />
        <Route path=":workspace_id" element={<Outlet />}>
          <Route index element={<DetailRedirection />} />
          <Route
            path="text-search-sandbox"
            element={<TextSearchSandboxContainer />}
          />
          <Route path="home" element={<WorkspaceHome />} />
          <Route path="product-ranking" element={<ProductRankingPage />} />
          <Route path="hey-syncly" element={<ChatPage />} />
          <Route path="studio">
            <Route index element={<Navigate to="../reports" replace />} />
            <Route
              path=":studio_item_alias"
              element={<StudioReportsRedirect />}
            />
          </Route>
          <Route path="reports">
            <Route index element={<ReportListPage />} />
            <Route
              path=":studio_item_alias"
              element={<StudioItemDetailPage />}
            />
          </Route>
          <Route
            path="signals"
            element={<SignalsLayout />}
          >
            <Route index element={<SignalsPage />} />
            <Route path=":issue_name" element={<IssueDetailPage />} />
            <Route
              path=":issue_name/static"
              element={<StaticIssueDetailPage />}
            />
          </Route>
          <Route
            path="insight-explorer"
            element={<InsightExplorerLayout />}
          >
            <Route path=":field_type">
              <Route path=":node_id" element={<InsightExplorerContent />} />
            </Route>
          </Route>
          {/* TODO: 레거시 /trendings 유입 정리 후 이 리다이렉트 삭제 필요 */}
          <Route path="trendings" element={<Navigate to="../signals" replace />} />
          <Route path="agents" element={<AgentPage />} />
          <Route
            path="agents/:agent_alias"
            element={<AgentDetailRedirection />}
          />
          <Route path="charts/create" element={<ChartCreatePage />} />
          <Route path="charts/:chart_alias" element={<ChartDetailPage />} />
          <Route path="companies" element={<CompanyPage />} />
          <Route
            path="companies/:company_alias"
            element={<CompanyDetailRedirection />}
          />
          <Route path="companies/upload" element={<CompanyUploadPage />} />
          <Route path="customers" element={<CustomerPage />} />
          <Route
            path="customers/:customer_alias"
            element={<CustomerDetailRedirection />}
          />
          <Route path="customers/upload" element={<CustomerUploadPage />} />
          <Route
            path="customer-sentiment"
            element={<CustomerSentimentPage />}
          />
          <Route path="feedbacks" element={<AllFeedbackPage />} />
          <Route path="feedbacks/upload" element={<FeedbackCsvUpload />} />
          <Route path="feedbacks/upload-json" element={<FeedbackJsonUpload />} />
          <Route
            path="feedbacks/:feedback_alias"
            element={<FeedbackDetailRedirection />}
          />
          <Route path="dashboard2">
            <Route index element={<Dashboard2IndexPage />} />
            <Route path="list">
              <Route
                index
                element={<Dashboard2ListPage />}
              />
            </Route>
            <Route path=":dashboard_alias" element={<Dashboard2DetailPage />} />
          </Route>
          <Route path="report">
            <Route index element={<Navigate to="../dashboard2/list" replace />} />
            <Route path=":report_alias" element={<ReportDetailPage />} />
          </Route>
          <Route path="workflow">
            <Route index element={<WorkflowPage />} />
            <Route
              path="create-periodic"
              element={<CreatePeriodicWorkflowPage />}
            />
            <Route
              path="create-realtime"
              element={<CreateRealtimeWorkflowPage />}
            />
            <Route
              path="create-subscription2"
              element={<CreateSubscription2WorkflowPage />}
            />
            <Route
              path="create-signal-alert"
              element={<CreateSignalAlertPage />}
            />
            <Route path=":workflow_alias" element={<WorkflowDetailPage />} />
          </Route>
          <Route path="taxonomy">
            <Route index element={<TaxonomyWorkspaceRoutePage />} />
            <Route path=":taxonomy_alias">
              <Route element={<TaxonomyDetailWorkspaceLayoutRoutePage />}>
                <Route path="feedback" element={<TaxonomyDetailWorkspaceRoutePage />} />
                <Route
                  path="topic/:topic_alias"
                  element={<TaxonomyDetailWorkspaceRoutePage />}
                />
              </Route>
            </Route>
          </Route>
          <Route path="exp" element={<ExpPage />} />
          {settingRoutes}
        </Route>
      </Route>
    </Route>
);

export default workspaceRoutes;
